import React, { useEffect, useMemo, useRef, useState } from "react";
import { AuthFrame } from "../components/AuthFrame.jsx";
import { postJson, readResponse, responseMessage } from "../lib/api.js";

export function CompleteRegistrationPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const redirectTimer = useRef(null);

  useEffect(() => {
    let active = true;
    async function validate() {
      if (!token) {
        setError("Invalid or missing invitation link.");
        setStep("error");
        return;
      }
      try {
        const response = await fetch(`/api/auth/validate-invite?token=${encodeURIComponent(token)}`);
        const data = await readResponse(response);
        if (!response.ok) throw new Error(responseMessage(data, "Link expired or invalid."));
        if (active) {
          setInvite(data);
          setStep("request");
        }
      } catch (validationError) {
        if (active) {
          setError(validationError.message || "Link expired or invalid.");
          setStep("error");
        }
      }
    }
    validate();
    return () => {
      active = false;
      if (redirectTimer.current) window.clearTimeout(redirectTimer.current);
    };
  }, [token]);

  async function requestOtp() {
    setBusy(true);
    setError("");
    try {
      const { response, data } = await postJson("/api/auth/request-invite-otp", { token });
      if (!response.ok) throw new Error(responseMessage(data, "Failed to send OTP"));
      setStep("complete");
    } catch (requestError) {
      setError(requestError.message || "Failed to send OTP");
    } finally {
      setBusy(false);
    }
  }

  async function completeInvite(event) {
    event.preventDefault();
    const fields = new FormData(event.currentTarget);
    const otp = String(fields.get("otp") || "");
    const password = String(fields.get("password") || "");
    const confirmPassword = String(fields.get("confirmPassword") || "");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const { response, data } = await postJson("/api/auth/complete-invite", { token, otp, password });
      if (!response.ok) throw new Error(responseMessage(data, "Verification failed"));
      setStep("success");
      redirectTimer.current = window.setTimeout(() => window.location.assign("/Account/Login"), 3000);
    } catch (completeError) {
      setError(completeError.message || "Verification failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Complete Registration" subtitle="Verify your invitation and create your supplier portal password.">
      {invite?.company && <div className="react-invite-company">{invite.company}</div>}

      {step === "loading" && <div className="react-spinner" aria-label="Validating invitation" />}

      {step === "request" && (
        <div>
          <div className="auth-field">
            <label>Email address</label>
            <input value={invite?.email || ""} readOnly />
          </div>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <button className="auth-primary-btn" type="button" disabled={busy} onClick={requestOtp}>
            {busy ? "Sending..." : "Request Verification Code"}
          </button>
        </div>
      )}

      {step === "complete" && (
        <form className="login-form" onSubmit={completeInvite}>
          <div className="auth-field">
            <label htmlFor="inviteOtp">Verification code</label>
            <input id="inviteOtp" name="otp" inputMode="numeric" autoComplete="one-time-code" required />
          </div>
          <div className="auth-field">
            <label htmlFor="invitePass">Password</label>
            <input id="invitePass" name="password" type="password" minLength="6" autoComplete="new-password" required />
          </div>
          <div className="auth-field">
            <label htmlFor="invitePassConf">Confirm password</label>
            <input id="invitePassConf" name="confirmPassword" type="password" minLength="6" autoComplete="new-password" required />
          </div>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <button className="auth-primary-btn" type="submit" disabled={busy}>
            {busy ? "Verifying..." : "Complete Registration"}
          </button>
        </form>
      )}

      {step === "success" && (
        <div className="auth-success" role="status">
          Registration completed. Redirecting you to login…
        </div>
      )}

      {step === "error" && <div className="auth-error" role="alert">{error}</div>}
    </AuthFrame>
  );
}
